import { ReactNode } from 'react'
import cn from 'classnames'
import { motion } from 'framer-motion'
import { Button, ContentCard } from '@forwardflow/ui-v2'
import { ArrowLeftMdIcon, ChevronRightIcon } from '@forwardflow/icons'

/**
 * The building blocks every setup screen is made of. Compact on purpose:
 * one card per screen where possible, small type, one primary action.
 */

export const Card = ({
   children,
   className,
   delay = 0,
   testId,
}: {
   children: ReactNode
   className?: string
   delay?: number
   testId?: string
}) => (
   <ContentCard
      className={cn('!px-5 !py-4 md:!px-6 md:!py-5 space-y-4', className)}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay }}
      data-testid={testId}
   >
      {children}
   </ContentCard>
)

/** The question a screen asks, in plain words, and one line of why. */
export const StepHeader = ({
   title,
   lede,
   right,
}: {
   title: ReactNode
   lede?: ReactNode
   right?: ReactNode
}) => (
   <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
         <h2 className="text-white font-semibold text-[17px] leading-snug">{title}</h2>
         {lede && <p className="text-[#99A1AF] text-sm mt-1 max-w-[560px]">{lede}</p>}
      </div>
      {right && <div className="shrink-0">{right}</div>}
   </div>
)

/** A small labelled block inside a card. */
export const Section = ({
   label,
   hint,
   children,
   className,
}: {
   label?: ReactNode
   hint?: ReactNode
   children: ReactNode
   className?: string
}) => (
   <div className={cn('space-y-2', className)}>
      {(label || hint) && (
         <div>
            {label && <p className="text-white/90 text-sm font-medium">{label}</p>}
            {hint && <p className="text-white/45 text-xs mt-0.5">{hint}</p>}
         </div>
      )}
      {children}
   </div>
)

/** Back on the left, the one action on the right, a note underneath. */
export const StepFooter = ({
   onBack,
   backDisabled,
   action,
   onAction,
   disabled,
   loading,
   note,
   secondary,
   testId,
}: {
   onBack?: () => void
   backDisabled?: boolean
   action: ReactNode
   onAction: () => void
   disabled?: boolean
   loading?: boolean
   note?: ReactNode
   /** A quiet alternative, e.g. "Do this later". */
   secondary?: { label: ReactNode; onClick: () => void }
   testId?: string
}) => (
   <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: 0.15 }}
      className="space-y-2"
   >
      <div className="flex items-center gap-3">
         {onBack && (
            <Button
               variant="secondary"
               size="middle"
               className="!h-11 !px-4"
               onClick={onBack}
               disabled={backDisabled}
            >
               <ArrowLeftMdIcon width={12} /> Back
            </Button>
         )}
         <Button
            size="middle"
            className="!h-11 flex-1"
            onClick={onAction}
            disabled={disabled}
            loading={loading}
            data-testid={testId}
         >
            {action} <ChevronRightIcon width={16} />
         </Button>
      </div>
      <div className="flex items-center justify-between gap-3 min-h-[18px]">
         <p className="text-white/40 text-xs">{note}</p>
         {secondary && (
            <button
               type="button"
               className="text-white/50 hover:text-white text-xs font-medium"
               onClick={secondary.onClick}
            >
               {secondary.label}
            </button>
         )}
      </div>
   </motion.div>
)

/** A removable or addable chip. */
export const Chip = ({
   children,
   onRemove,
   onAdd,
   selected,
   disabled,
   title,
   testId,
   kind,
}: {
   children: ReactNode
   onRemove?: () => void
   onAdd?: () => void
   selected?: boolean
   disabled?: boolean
   title?: string
   testId?: string
   /** Small type label, e.g. "stage" or "tag". */
   kind?: string
}) => {
   const base =
      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[13px] leading-5 transition-colors'
   if (onAdd) {
      return (
         <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onAdd}
            data-testid={testId}
            className={cn(
               base,
               'border-[#9F7BFF]/40 bg-[#9F7BFF]/10 text-white hover:bg-[#9F7BFF]/25',
               disabled && 'opacity-40 cursor-not-allowed'
            )}
         >
            <span className="text-[#C9B8FF]">+</span>
            {children}
            {kind && <span className="text-white/40 text-[10px] uppercase tracking-wide">{kind}</span>}
         </button>
      )
   }
   return (
      <span
         title={title}
         data-testid={testId}
         className={cn(
            base,
            selected === false
               ? 'border-white/10 bg-white/[0.03] text-white/60'
               : 'border-white/15 bg-white/[0.08] text-white'
         )}
      >
         {children}
         {kind && <span className="text-white/40 text-[10px] uppercase tracking-wide">{kind}</span>}
         {onRemove && (
            <button
               type="button"
               aria-label={`Remove ${typeof children === 'string' ? children : ''}`}
               className="ml-0.5 w-4 h-4 rounded-full bg-white/10 hover:bg-white/30 text-white/70 hover:text-white text-[11px] leading-none flex items-center justify-center"
               onClick={onRemove}
            >
               ×
            </button>
         )}
      </span>
   )
}

/** A selectable option tile (radio-like). */
export const OptionTile = ({
   selected,
   onClick,
   children,
   className,
   testId,
   disabled,
   role = 'radio',
}: {
   selected: boolean
   onClick: () => void
   children: ReactNode
   className?: string
   testId?: string
   disabled?: boolean
   role?: 'radio' | 'checkbox' | 'button'
}) => (
   <button
      type="button"
      role={role}
      aria-checked={role === 'button' ? undefined : selected}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={cn(
         'text-left rounded-xl border p-3.5 transition-all w-full',
         selected
            ? 'border-[#9F7BFF] bg-[rgba(159,123,255,0.10)]'
            : 'border-white/10 bg-white/[0.02] hover:border-white/25',
         disabled && 'opacity-40 cursor-not-allowed',
         className
      )}
   >
      {children}
   </button>
)

/** A quiet inline text button, e.g. "Change", "Edit". */
export const LinkButton = ({
   children,
   onClick,
   disabled,
   className,
   testId,
}: {
   children: ReactNode
   onClick: () => void
   disabled?: boolean
   className?: string
   testId?: string
}) => (
   <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={cn(
         'text-[#A684FF] text-sm font-medium hover:underline shrink-0 disabled:opacity-40 disabled:no-underline',
         className
      )}
   >
      {children}
   </button>
)

export const Skeleton = ({ className }: { className?: string }) => (
   <div className={cn('rounded-lg bg-white/10 animate-pulse', className)} />
)
